import React, { useEffect, useState, useRef } from "react";
import useAxiosInstance from "../../../../../Utilities/Hooks/useAxiosInstance";
import { ORDERS_API, PRODUCTSIZE_API } from "../../../../../Utilities/APIs/APIs";
// import "./ScanOrderItems.css";

function ScanOrderItems({ orderId, setShowScanModal }) {
  const axiosInstance = useAxiosInstance();

  // base path for stock units (axiosInstance already has /api prefix)
  const STOCKUNITS_BASE = "/inventory/stockUnits/";

  const [lines, setLines] = useState([]); // each line now also has cancelledQty + status
  const [totalOrdered, setTotalOrdered] = useState(0); // only NON-cancelled qty
  const [totalScanned, setTotalScanned] = useState(0);

  const [barcode, setBarcode] = useState("");
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: string }
  const [loading, setLoading] = useState(false);

  // contains BOTH: previously scanned (from DB) + new ones from this session
  const [scannedCodes, setScannedCodes] = useState([]);

  // --- audio refs ---
  const successSoundRef = useRef(null);
  const errorSoundRef = useRef(null);

  useEffect(() => {
    // preload sounds once
    successSoundRef.current = new Audio("/sounds/scan-ok.mp3");
    errorSoundRef.current = new Audio("/sounds/scan-error.mp3");

    successSoundRef.current.volume = 0.6;
    errorSoundRef.current.volume = 0.6;
  }, []);

  function playOkBeep() {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(700, ctx.currentTime);
    osc1.frequency.exponentialRampToValueAtTime(
      1200,
      ctx.currentTime + 0.12
    );
    gain1.gain.setValueAtTime(0.8, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
    osc1.connect(gain1).connect(ctx.destination);
    osc1.start();
    osc1.stop(ctx.currentTime + 0.15);

    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "triangle";
    osc2.frequency.value = 1500;
    gain2.gain.setValueAtTime(0.4, ctx.currentTime + 0.03);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
    osc2.connect(gain2).connect(ctx.destination);
    osc2.start(ctx.currentTime + 0.03);
    osc2.stop(ctx.currentTime + 0.12);
  }

  function playErrorBeep() {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    function pip(at, freq) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "square";
      osc.frequency.value = freq;

      gain.gain.setValueAtTime(0.9, at);
      gain.gain.exponentialRampToValueAtTime(0.1, at + 0.1);

      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.12);
    }

    const now = ctx.currentTime;
    pip(now, 900);
    pip(now + 0.15, 1100);
  }

  function showSuccess(text) {
    playOkBeep();
    setMessage({ type: "success", text });
  }

  function showError(text) {
    playErrorBeep();
    setMessage({ type: "error", text });
  }

// --- Load order items (and scanned state) once ---
useEffect(() => {
  async function loadOrderItems() {
    try {
      if (!orderId) return;

      const { data } = await axiosInstance.get(
        ORDERS_API + "orderItemsFromOrder/" + orderId
      );

      const allCodes = [];

      const mapped = (data.orderItems || []).map((oi) => {
        const qtyRaw = Number(oi.quantity || 0);
        const cancelQty = Number(oi.cancelQuantity || 0);
        const effectiveQty = Math.max(0, qtyRaw - cancelQty); // 🔐 active qty only
        const scannedQty = Number(oi.scannedQuantity || 0);

        if (Array.isArray(oi.scannedBarcodes)) {
          allCodes.push(...oi.scannedBarcodes);
        }

        return {
          lineId: oi._id,
          productId: oi.item?._id,
          sizeId: oi.size?._id,
          name: oi.item?.name || "",
          sizeName: oi.size?.name || "",
          orderedQty: effectiveQty,                 
          scannedQty,                              
          cancelledQty: cancelQty,
          isCancelled: !!oi.isCancelled,
        };
      });

      const orderedTotal = mapped.reduce(
        (sum, l) => sum + l.orderedQty,
        0
      );
      const scannedTotal = mapped.reduce(
        (sum, l) => sum + Math.min(l.scannedQty, l.orderedQty),
        0
      );

      setLines(mapped);
      setTotalOrdered(orderedTotal);
      setTotalScanned(scannedTotal);
      setMessage(null);
      setScannedCodes(Array.from(new Set(allCodes)));
    } catch (e) {
      console.error("loadOrderItems error", e);
      setMessage({
        type: "error",
        text: "Failed to load order items.",
      });
    }
  }

  loadOrderItems();
}, [axiosInstance, orderId]);


  const remaining = totalOrdered - totalScanned;

  // --- Scan handler ---------------------------------------------------
  async function handleAdd(e) {
    e?.preventDefault();
    const inputCode = barcode.trim();
    if (!inputCode) return;

    setLoading(true);
    try {
      // 1) Lookup stock unit by barcode (with smart backend fallback)
      const suRes = await axiosInstance.get(
        STOCKUNITS_BASE + "by-code/" + encodeURIComponent(inputCode)
      );
      const stockUnit = suRes.data;

      if (!stockUnit || !stockUnit.productSize) {
        showError("Barcode not recognised.");
        setLoading(false);
        return;
      }

      const resolvedCode = stockUnit.code; // canonical code from DB

      if (scannedCodes.includes(resolvedCode)) {
        showError("This barcode has already been scanned for this order.");
        setBarcode("");
        setLoading(false);
        return;
      }

      if (stockUnit.status && stockUnit.status !== "available") {
        showError("This barcode is already used / sold.");
        setLoading(false);
        return;
      }

      // 2) Get product + size from ProductSize
      const psRes = await axiosInstance.get(
        PRODUCTSIZE_API + "getSingleProductSize/" + stockUnit.productSize
      );
      const ps = psRes.data;

      const productId =
        ps.product && (ps.product._id || ps.product.id || ps.product);
      const sizeId = ps.size && (ps.size._id || ps.size.id || ps.size);

      if (!productId || !sizeId) {
        showError("Could not resolve product/size for this barcode.");
        setLoading(false);
        return;
      }

      // 3) Match against this order's lines
      setLines((prevLines) => {
      const idx = prevLines.findIndex(
        (l) =>
          String(l.productId) === String(productId) &&
          String(l.sizeId) === String(sizeId)
      );

      if (idx === -1) {
        showError("This barcode is not part of this order.");
        setLoading(false);
        return prevLines;
      }

      const line = prevLines[idx];
      const limit = Number(line.orderedQty || 0); // 🔐 active qty after cancellation

      if (limit <= 0) {
        showError("This product/size is cancelled from this order.");
        setLoading(false);
        return prevLines;
      }

      if (line.scannedQty >= limit) {
        showError(
          `All active units for "${line.name}" (${line.sizeName}) are already scanned.`
        );
        setLoading(false);
        return prevLines;
      }

      // 4) Accept scan – update counts
      const newLines = [...prevLines];
      newLines[idx] = {
        ...line,
        scannedQty: line.scannedQty + 1,
      };

      setTotalScanned((prev) => prev + 1);
      setScannedCodes((prev) => [...prev, resolvedCode]);

      if (inputCode.toLowerCase() !== resolvedCode.toLowerCase()) {
        showSuccess(`Scan recorded (matched as ${resolvedCode}).`);
      } else {
        showSuccess("Scan recorded.");
      }

      setBarcode("");
      setLoading(false);
      return newLines;
    });

    } catch (err) {
      console.error("scan error", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to validate barcode.";
      showError(msg);
      setLoading(false);
    }
  }

  // --- Confirm all ----------------------------------------------------
  async function handleConfirmAll() {
    if (remaining > 0) {
      showError(
        "You still have unscanned items. Please scan all before confirming."
      );
      return;
    }
    if (!orderId) {
      showError("Missing order ID.");
      return;
    }

    try {
      setLoading(true);
      const uniqueCodes = Array.from(new Set(scannedCodes));

      await axiosInstance.patch(ORDERS_API + "markPacked/" + orderId, {
        codes: uniqueCodes,
      });

      showSuccess("All items scanned. Stock updated and order marked as packed.");
      setTimeout(() => setShowScanModal(false), 800);
    } catch (err) {
      console.error("confirm-all error", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to confirm packing.";
      showError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="scan-modal">
      <div className="scan-modal-header">
        <h2 className="scan-modal-title">Scan &amp; Pack</h2>
      </div>

      <div className="scan-modal-body">
        <div className="scan-summary">
          <p>
            <strong>Total ordered:</strong> <span>{totalOrdered}</span>
          </p>
          <p>
            <strong>Scanned:</strong> <span>{totalScanned}</span>
          </p>
          <p>
            <strong>Remaining:</strong> <span>{remaining}</span>
          </p>

          {message && (
            <div
              className={
                "scan-alert " +
                (message.type === "success"
                  ? "scan-alert-success"
                  : "scan-alert-error")
              }
            >
              {message.text}
            </div>
          )}
        </div>

        <form className="scan-input-row" onSubmit={handleAdd}>
          <input
            type="text"
            className="scan-input"
            placeholder="Scan / type barcode"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            disabled={loading}
          />
          <button
            type="submit"
            className="scan-add-btn"
            disabled={loading || !barcode.trim()}
          >
            {loading ? "Checking..." : "Add"}
          </button>
        </form>

        <div className="scan-table-wrapper">
          <table className="scan-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Size</th>
                <th>Ordered</th>
                <th>Scanned</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
            {lines.length === 0 ? (
                <tr>
                  <td colSpan="5" className="scan-empty-row">
                    No items / summary not loaded.
                  </td>
                </tr>
              ) : (
                lines.map((line) => {
                  const effectiveOrdered = Number(line.orderedQty || 0); // qty after cancellations
                  const scanned = Number(line.scannedQty || 0);

                  let statusLabel = "pending";

                  if (effectiveOrdered === 0 || line.isCancelled) {
                    statusLabel = "Canceled";
                  } else if (scanned >= effectiveOrdered) {
                    statusLabel = "Scanned";
                  }

                  return (
                    <tr key={line.lineId}>
                      <td>{line.name}</td>
                      <td>{line.sizeName}</td>
                      <td>{effectiveOrdered}</td>
                      <td>{scanned}</td>
                      <td>{statusLabel}</td>
                    </tr>
                  );
                })
              )}
          </tbody>
          </table>
        </div>
      </div>

      <div className="scan-modal-footer">
        <button
          type="button"
          className="scan-close-btn"
          onClick={() => setShowScanModal(false)}
        >
          Close
        </button>
        <button
          type="button"
          className="scan-confirm-btn"
          onClick={handleConfirmAll}
          disabled={loading || totalOrdered === 0}
        >
          {loading ? "Processing..." : "Confirm"}
        </button>
      </div>
    </div>
  );
}

export default ScanOrderItems;
